import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  Check, ArrowRight, Truck, Wrench, Search, Shield,
  Clipboard, Phone, HelpCircle, ChevronDown, ChevronUp,
  Car, Headphones
} from 'lucide-react'
import { useState } from 'react'

export default function Pricing() {
  const { user } = useAuth()
  const [expandedFaq, setExpandedFaq] = useState(null)

  if (user) {
    return <Navigate to="/" replace />
  }

  const conciergeServices = [
    {
      icon: Truck,
      name: 'Transport Coordination',
      example: 'e.g. $2,000 carrier + $400 fee',
      desc: 'We get multi-carrier quotes, schedule pickup and delivery, and handle condition documentation.',
    },
    {
      icon: Wrench,
      name: 'Service & Maintenance',
      example: 'e.g. $500 shop bill + $100 fee',
      desc: 'We find the right shop, schedule the work, and follow up through completion.',
    },
    {
      icon: Search,
      name: 'Pre-Purchase Inspection',
      example: 'e.g. $300 inspector + $60 fee',
      desc: 'We coordinate a trusted inspector, schedule the visit, and deliver the report.',
    },
    {
      icon: Shield,
      name: 'Insurance Placement',
      example: 'e.g. $1,500 premium + $300 fee',
      desc: 'We connect you with agreed-value specialists and shop multiple carriers on your behalf.',
    },
    {
      icon: Clipboard,
      name: 'Restoration Oversight',
      example: 'e.g. $8,000/mo shop + $1,600/mo fee',
      desc: 'Ongoing project management — budget tracking, milestone updates, quality checks, shop communication.',
    },
    {
      icon: Phone,
      name: 'On-Call Support',
      example: 'Included with any engagement',
      desc: 'Direct line to your concierge. No phone trees, no waiting. Available whenever you have an active request.',
    },
  ]

  const freeFeatures = [
    'Unlimited vehicle profiles',
    'Document & photo vault',
    '3 AI market valuations per month',
    'Complete service history tracking',
    'Maintenance reminders',
    'Provenance score tracking',
  ]

  const faqItems = [
    {
      question: "How does the 20% service fee work?",
      answer: "When you request any service through the concierge, we coordinate everything with the vendor. You pay the vendor's price for their work, plus a 20% service fee to RDC for the coordination. For example, if a transport carrier charges $2,000, your total is $2,400 — the carrier's $2,000 plus our $400 service fee."
    },
    {
      question: "Are there any hidden fees or markups beyond the 20%?",
      answer: "No. The 20% service fee is the only cost from us. We don't mark up vendor pricing, add administrative fees, or charge retainers. You always see the vendor's cost and our fee separately."
    },
    {
      question: "Do I need a subscription to use concierge?",
      answer: "No subscription required. Sign up for a free digital garage account, then request concierge services whenever you need them. You only pay when you use us."
    },
    {
      question: "What if I just want the free digital garage?",
      answer: "That's completely fine. The digital garage is free forever — unlimited vehicles, document storage, service tracking, and maintenance reminders. Use it as long as you want with no obligation to use concierge services."
    },
    {
      question: "How do I request a service?",
      answer: "Open the Concierge chat in your dashboard and describe what you need. Our team will create a work order, gather any details needed, and handle coordination. You approve each step before we proceed."
    },
    {
      question: "What areas do you serve?",
      answer: "We're based in Millbrook, NY and currently serve the Hudson Valley and tri-state area for hands-on services. Transport coordination and remote services (insurance, inspections) are available nationwide."
    }
  ]

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index)
  }

  return (
    <div className="min-h-screen bg-rdc-cream">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-rdc-stone/20">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-3">
            <img
              src="/RDC_Logomark_Orange.png"
              alt="Riding & Driving Club"
              className="h-10 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium text-rdc-charcoal hover:text-black transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="px-5 py-2 text-sm font-medium bg-rdc-orange text-white rounded-full hover:bg-rdc-orange/90 transition-colors"
            >
              Get Started Free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10">
        <div className="text-center">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-black mb-5">
            Pay Only When You Need Us
          </h1>
          <p className="text-lg text-rdc-charcoal max-w-2xl mx-auto">
            The digital garage is free. Concierge services are vendor cost + a 20% service fee — no subscriptions, no hidden charges, no surprises.
          </p>
        </div>
      </section>

      {/* Two-column: Free + Concierge overview */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Free Tier */}
          <div className="bg-white rounded-2xl p-8 border border-rdc-stone/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-rdc-orange/10 flex items-center justify-center">
                <Car size={20} className="text-rdc-orange" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-black">Digital Garage</h2>
                <p className="text-xs text-rdc-taupe">Free forever</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-black mb-6">$0</p>
            <ul className="space-y-3">
              {freeFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={18} className="text-rdc-orange flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-rdc-charcoal">{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/signup"
              className="mt-8 w-full flex items-center justify-center gap-2 px-6 py-3 bg-rdc-cream border border-rdc-stone/30 text-black font-semibold rounded-full hover:bg-rdc-stone/10 transition-colors text-sm"
            >
              Create Free Account
            </Link>
          </div>

          {/* Concierge Overview */}
          <div className="bg-gradient-to-br from-rdc-charcoal to-rdc-charcoal/95 rounded-2xl p-8 border border-rdc-orange/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-rdc-orange/20 flex items-center justify-center">
                <Headphones size={20} className="text-rdc-orange" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-white">Concierge Services</h2>
                <p className="text-xs text-rdc-stone">Pay as you go</p>
              </div>
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <p className="text-4xl font-bold text-white">20%</p>
              <p className="text-white/60 text-lg">service fee</p>
            </div>
            <p className="text-sm text-rdc-stone mb-6">On every engagement. No subscription.</p>
            <p className="text-sm text-rdc-stone/80 leading-relaxed mb-6">
              You pay the vendor's price for any service, plus a flat 20% coordination fee. We handle everything — sourcing providers, getting quotes, scheduling, and follow-through. You approve each step before we proceed.
            </p>
            <div className="space-y-2 mb-6">
              {['Transport coordination', 'Service & maintenance', 'Pre-purchase inspections', 'Insurance placement', 'Restoration oversight', 'On-call support included'].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Check size={14} className="text-rdc-orange flex-shrink-0" />
                  <span className="text-sm text-white/80">{item}</span>
                </div>
              ))}
            </div>
            <Link
              to="/signup"
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-rdc-orange text-white font-semibold rounded-full hover:bg-rdc-orange/90 transition-colors text-sm"
            >
              Get Started
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Detailed Rate Card */}
      <section className="bg-white border-y border-rdc-stone/20">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold text-black mb-3">
              How the 20% Fee Works
            </h2>
            <p className="text-rdc-charcoal max-w-xl mx-auto">
              You pay the vendor directly for their service. Our 20% coordination fee is added on top — that's the only cost from us.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {conciergeServices.map(({ icon: Icon, name, example, desc }, i) => (
              <div key={i} className="rounded-xl border border-rdc-stone/15 p-6 bg-rdc-cream/30">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-rdc-orange/10 flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-rdc-orange" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-black">{name}</h3>
                    <p className="text-xs text-rdc-orange font-medium mt-0.5">{example}</p>
                  </div>
                </div>
                <p className="text-sm text-rdc-charcoal leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works — simple 3-step */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl font-bold text-black mb-3">
            How It Works
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {[
            { step: '1', title: 'Describe what you need', desc: 'Open the concierge chat and tell us what you need — transport, service, inspection, insurance, anything.' },
            { step: '2', title: 'We handle coordination', desc: 'We create a work order, source providers, get quotes, and present your options. You approve before we proceed.' },
            { step: '3', title: 'You approve, we execute', desc: "You see the vendor's cost plus our 20% service fee upfront. Approve it, and we handle everything through completion." },
          ].map(({ step, title, desc }, i) => (
            <div key={i} className="text-center">
              <div className="w-12 h-12 rounded-full bg-rdc-orange text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                {step}
              </div>
              <h3 className="font-display font-bold text-black mb-2">{title}</h3>
              <p className="text-sm text-rdc-charcoal leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-rdc-stone/20">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="font-display text-3xl font-bold text-black mb-10 text-center">
            Questions
          </h2>
          <div className="space-y-3">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-rdc-cream/50 rounded-xl border border-rdc-stone/15 overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-rdc-cream transition"
                >
                  <div className="flex items-start gap-3 text-left">
                    <HelpCircle size={18} className="text-rdc-orange flex-shrink-0 mt-0.5" />
                    <p className="font-semibold text-black text-sm">{item.question}</p>
                  </div>
                  {expandedFaq === index ? (
                    <ChevronUp size={18} className="text-rdc-orange flex-shrink-0" />
                  ) : (
                    <ChevronDown size={18} className="text-rdc-taupe flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-4 pt-0">
                    <p className="text-sm text-rdc-charcoal leading-relaxed pl-[30px]">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-rdc-charcoal to-black">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-4">
            Start With a Free Digital Garage
          </h2>
          <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">
            Add your vehicles, build your records, and use concierge whenever you need us. No commitment required.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-rdc-orange text-white font-semibold rounded-full hover:bg-rdc-orange/90 transition-colors text-lg"
            >
              Create Free Account
              <ArrowRight size={20} />
            </Link>
            <a
              href="mailto:michael@ridinganddrivingclub.com"
              className="inline-flex items-center gap-2 px-8 py-4 bg-transparent text-white font-semibold rounded-full border-2 border-white/40 hover:bg-white/10 transition-colors text-lg"
            >
              Questions? Email Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-rdc-taupe text-white/70">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img
                src="/RDC_Logomark_Orange.png"
                alt="RDC"
                className="h-8 w-auto brightness-0 invert opacity-70"
              />
              <span className="font-display font-bold text-white">Riding & Driving Club</span>
            </div>
            <div className="flex items-center gap-6 text-sm flex-wrap justify-center md:justify-end">
              <Link to="/welcome" className="hover:text-white transition-colors">Home</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/login" className="hover:text-white transition-colors">Sign In</Link>
            </div>
            <p className="text-sm">&copy; {new Date().getFullYear()} Riding & Driving Club</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
